import { DatePipe } from '@angular/common';
import { Component, computed, inject, linkedSignal, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { RaLoadingComponent } from '@shared/components';
import { DocumentItemTypeEnum } from 'src/app/domain/enums/documents/document-item-type.enum';
import { RaButtonComponent } from 'src/app/shared/components/ra-button/ra-button.component';
import { RaFilletComponent } from 'src/app/shared/components/ra-fillet/ra-fillet.component';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';
import { CardTypeFormatPipe } from 'src/app/shared/pipes/card-type-format.pipe';
import { deltaToPlainText } from 'src/app/shared/utils/delta.util';
import { EditDataService } from '../../../services/_edit-data.service';
import { EditItemsService } from '../../../services/edit-items.service';

/** Destino possível para mover a seleção: nível de uma seção OU uma pasta de checklist. */
interface MoveDestination {
    key: string;
    label: string;
    sublabel?: string;
    sessionId: string;
    folderId?: string;
    isFolder: boolean;
}

@Component({
    selector: 'modal-list-section',
    templateUrl: 'modal-list-section.component.html',
    styleUrl: 'modal-list-section.component.scss',
    imports: [
        RaModalComponent,
        RaButtonComponent,
        RaLoadingComponent,
        RaFilletComponent,
        TranslatePipe,
    ],
    providers: [DatePipe, CardTypeFormatPipe],
})
export class ModalListSectionComponent {
    public close = output<void>();
    public loading = signal(false);

    // * [Injects]
    public editDataService = inject(EditDataService);
    private _editItemsService = inject(EditItemsService);

    /**
     * Destinos do move: todas as seções (nível) + as pastas (CHECKLIST_FOLDER) de
     * qualquer seção, incluindo as da seção atual. Exclui a pasta de origem (aquela em
     * que a seleção já está) e o "nível da seção atual" quando não estamos dentro de
     * uma pasta (seria mover para onde já está).
     */
    public destinations = computed<MoveDestination[]>(() => {
        const sessions = this.editDataService.document()?.sessions ?? [];
        const currentSessionId = this.editDataService.currentSession()?._id;
        const currentFolderId = this.editDataService.currentFolderId();

        const dests: MoveDestination[] = [];

        for (const section of sessions) {
            const isCurrent = section._id === currentSessionId;

            // Destino "nível da seção":
            //  - outras seções → sempre (comportamento legado)
            //  - seção atual → só quando estamos DENTRO de uma pasta (= tirar da pasta)
            if (!isCurrent) {
                dests.push({
                    key: `s:${section._id}`,
                    label: section.name,
                    sublabel: section.description,
                    sessionId: section._id,
                    isFolder: false,
                });
            } else if (currentFolderId) {
                dests.push({
                    key: `s:${section._id}`,
                    label: section.name,
                    sessionId: section._id,
                    isFolder: false,
                });
            }

            // Destinos "pasta" desta seção (exceto a pasta de origem).
            const folders = (section.items ?? []).filter(
                item => item.type === DocumentItemTypeEnum.CHECKLIST_FOLDER
            );
            for (const folder of folders) {
                if (isCurrent && folder._id === currentFolderId) continue;
                dests.push({
                    key: `f:${folder._id}`,
                    label: folder.name,
                    sublabel: section.name,
                    sessionId: section._id,
                    folderId: folder._id,
                    isFolder: true,
                });
            }
        }

        return dests;
    });

    /**
     * Nome sugerido para a seção nova: o texto do primeiro título (TITLE) da seleção, na
     * ordem da seção. Sem título, vazio (o servidor usa o nome padrão).
     */
    private _suggestedName = computed(() => {
        const selected = new Set(this.editDataService.selectedItems().map(item => item._id));
        const title = (this.editDataService.currentSession()?.items ?? []).find(
            item => selected.has(item._id) && item.type === DocumentItemTypeEnum.TITLE
        );
        return title ? deltaToPlainText(title.value).replace(/\s+/g, ' ').trim() : '';
    });

    /** Campo do nome: começa com a sugestão e fica com o que o usuário digitar. */
    public newSectionName = linkedSignal(() => this._suggestedName());

    /** Pastas como destino (agrupadas separadamente das seções no modal). */
    public folderDestinations = computed(() => this.destinations().filter(d => d.isFolder));

    /** Seções (nível) como destino. */
    public sectionDestinations = computed(() => this.destinations().filter(d => !d.isFolder));

    /** Cria a seção logo depois da atual e move a seleção para ela. */
    public moveToNewSection(): void {
        if (this.loading()) return;
        this.loading.set(true);
        this._editItemsService.moveItemsIntoNewSession(this.newSectionName()).subscribe({
            next: () => {
                this.loading.set(false);
                this.close.emit();
            },
            error: () => this.loading.set(false),
            complete: () => this.loading.set(false),
        });
    }

    public move(dest: MoveDestination): void {
        this.loading.set(true);
        this._editItemsService.moveItems(dest.sessionId, dest.folderId).subscribe({
            next: () => {
                this.loading.set(false);
                this.close.emit();
            },
            error: () => this.loading.set(false),
        });
    }
}
